import { useState } from 'react';
import { Button, addToast } from '@heroui/react';
import { useSelector } from 'react-redux';
import { MessageComposer } from '@/features/MessageComposer';
import { IAssignmentSubmissionComment } from '@/entities/Assignment';
import {
  useCreateSubmissionCommentMutation,
  ISubmissionCommentRequest,
} from '@/entities/SubmissionComment';
import { getMyProfile } from '@/entities/Profile';
import { SubmissionComment } from './SubmissionComment';

interface ISubmissionCommentsProps {
  comments: IAssignmentSubmissionComment[];
  submissionId: number;
  canAddComment: boolean;
  canGrade?: boolean;
  handleOpenGradeModal?: () => void;
}

export const SubmissionComments = ({
  comments,
  submissionId,
  canAddComment,
  canGrade = false,
  handleOpenGradeModal,
}: ISubmissionCommentsProps) => {
  const user = useSelector(getMyProfile);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [createComment, { isLoading: isSubmitting }] = useCreateSubmissionCommentMutation();

  const handleSubmitComment = async (data: ISubmissionCommentRequest) => {
    try {
      await createComment({
        submissionId,
        data,
      }).unwrap();

      addToast({
        title: 'Comment added successfully',
        color: 'success',
      });

      setIsComposerOpen(false);
    } catch (error: any) {
      addToast({
        title: error?.data?.message || 'Failed to add comment',
        color: 'danger',
      });
    }
  };

  const handleToggleComposer = () => {
    setIsComposerOpen((prev) => !prev);
  };

  return (
    <>
      {comments?.length > 0 && (
        <div>
          <h3 className="font-medium mb-3">Discuss the submission</h3>
          <ul className="flex flex-col gap-3">
            {comments.map((comment) => (
              <SubmissionComment
                key={comment.id}
                comment={comment}
                userId={user?.id ?? null}
                asListItem
              />
            ))}
          </ul>
        </div>
      )}

      {isComposerOpen && canAddComment && (
        <MessageComposer
          withLayout
          draftKey={`submission-comment:${submissionId}`}
          onSubmit={handleSubmitComment}
          isSubmitting={isSubmitting}
        />
      )}

      <div className="flex items-center gap-3">
        {canAddComment && (
          <Button
            radius="full"
            className="self-start bg-[#211D55] text-white min-w-[160px]"
            onPress={handleToggleComposer}
            isDisabled={isSubmitting}
          >
            {isComposerOpen ? 'Cancel comment' : 'Add comment'}
          </Button>
        )}
        {canGrade && Boolean(handleOpenGradeModal) && (
          <Button
            variant="bordered"
            className="border-[#211D55] text-[#211D55] bg-white font-medium self-start min-w-[160px]"
            radius="full"
            onPress={handleOpenGradeModal}
          >
            Grade
          </Button>
        )}
      </div>
    </>
  );
};
